import { isSuccess } from '@devexperts/remote-data-ts'
import { useTranslation } from 'react-i18next'

import { BookingAvailability } from '@/store/bookingAvailabilitySlice'
import { selectCartMapSpots } from '@/store/cartSlice/selectors/selectCartMapSpots.ts'
import { extraSlice } from '@/store/extraSlice'
import { getObjectsComparison } from '@/utils/getObjectsComparison'
import { normalizeReservation } from '@/utils/normalizeReservation'
import { normalizeSpot } from '@/utils/normalizeSpot'
import { ExclamationMark } from '../../../assets/icons/ExclamationMark'
import { useAppSelector } from '../../../hooks/store'
import { appSlice } from '../../../store/appSlice'
import { cartSlice } from '../../../store/cartSlice'
import { mapSelectors } from '../../../store/mapSlice'
import { priceFormatter } from '../../../utils/price'
import { ServiceCostDialog } from '../../ServiceCostDialog'

import { CartPopupSpot } from './CartPopupSpot'

export const CartPopupContent = () => {
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const total = useAppSelector(cartSlice.selectors.total)
  const fees = useAppSelector(cartSlice.selectors.quoteFees)
  const sectors = useAppSelector(mapSelectors.sectors)
  const offersTotal = useAppSelector(cartSlice.selectors.offersTotal)
  const isKiosk = useAppSelector(appSlice.selectors.isKiosk)
  const mapSpots = useAppSelector(selectCartMapSpots)

  const isWeatherInsuranceAddedToCart = useAppSelector(
    cartSlice.selectors.isWeatherInsuranceAddedToCart
  )

  const weatherInsurancePricePreview = useAppSelector(
    extraSlice.selectors.weatherInsurancePricePreview
  )

  const selectedBookingType = useAppSelector(
    cartSlice.selectors.selectedBookingType
  )
  const { t } = useTranslation()

  if (!cartReservations.length) return null

  return (
    <div className="">
      <div className="flex flex-col gap-4 lg:max-h-80 lg:overflow-hidden">
        <div className="lg:overflow-auto">
          {cartReservations?.map((reservation) => {
            const spot = mapSpots?.find((spot) => {
              return getObjectsComparison(
                normalizeReservation(reservation),
                normalizeSpot(spot)
              )
            })

            if (!spot) return null

            const sectorImage = sectors.find(
              (sector) => sector.header.id === spot.sectorId
            )?.header.image

            return (
              <CartPopupSpot
                key={spot.id}
                reservation={reservation}
                sectorImage={sectorImage}
                spot={spot}
              />
            )
          })}
        </div>

        <div className="bg-white pb-4 lg:sticky lg:bottom-0">
          <div className="flex justify-between">
            <span className="text-sm text-secondary">
              {selectedBookingType === BookingAvailability.TICKETS
                ? t('common.tickets')
                : t('map.elementDetail.spot', { count: 2 })}
              {/* Please note: this count does not reflect a real dynamic value,
              we just needed any number greater than one to obtain the translation
              in plural form */}
            </span>
            <span className="text-sm text-secondary">
              {priceFormatter.format(total - fees)}
            </span>
          </div>
          {isSuccess(weatherInsurancePricePreview) &&
          isWeatherInsuranceAddedToCart ? (
                <div className="flex justify-between">
                  <span className="text-sm text-secondary">
                    {t('extra.weatherInsurance.title')}
                  </span>
                  <span className="text-sm text-secondary">
                    {priceFormatter.format(weatherInsurancePricePreview.value)}
                  </span>
                </div>
              ) : null}
          {!isKiosk && fees > 0 ? (
            <div className="flex justify-between">
              <ServiceCostDialog
                trigger={
                  <button aria-label={t('common.open')}>
                    <span className="inline-flex items-center gap-1 text-sm text-secondary underline">
                      {t('map.elementDetail.serviceCost.title')}
                      <span className="text-base text-primary">
                        <ExclamationMark className="size-4" />
                      </span>
                    </span>
                  </button>
                }
              />
              <span className="text-sm text-secondary">
                {priceFormatter.format(fees)}
              </span>
            </div>
          ) : null}
          {offersTotal > 0 ? (
            <div className="flex justify-between">
              <span className="text-sm text-secondary">
                {t('map.elementDetail.offer')}
              </span>
              <span className="text-sm text-secondary">
                -{priceFormatter.format(offersTotal)}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
